import { useState } from 'react'
import type { FinalDiff, DiffStats } from '@/lib/api'
import { ChevronDown, ChevronUp, Plus, Minus, ArrowRight, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'

interface DDiffViewerProps {
  finalDiff: FinalDiff
  onClose: () => void
}

interface Change {
  type: 'added' | 'deleted' | 'modified'
  content: string
  modifications?: Array<{
    deleted: string
    added: string
  }>
}

function DiffStatsCard({ stats }: { stats: DiffStats }) {
  return (
    <div className="flex items-center gap-4 text-xs font-light">
      {stats.blocks_added > 0 && (
        <div className="flex items-center gap-1.5" style={{ color: 'hsl(142 50% 35%)' }}>
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>{stats.blocks_added} added</span>
        </div>
      )}
      {stats.blocks_deleted > 0 && (
        <div className="flex items-center gap-1.5" style={{ color: 'hsl(0 50% 45%)' }}>
          <Minus className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>{stats.blocks_deleted} deleted</span>
        </div>
      )}
      {stats.blocks_modified > 0 && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>{stats.blocks_modified} modified</span>
        </div>
      )}
    </div>
  )
}

function AddedBlock({ content }: { content: string }) {
  return (
    <div className="rounded-md overflow-hidden">
      <div
        className="px-3 py-2 text-sm"
        style={{
          backgroundColor: 'hsl(142 50% 50% / 0.1)',
          borderLeft: '3px solid hsl(142 50% 45%)',
        }}
      >
        <div className="font-serif leading-relaxed prose-diff-added" style={{ color: 'hsl(142 50% 25%)' }}>
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      </div>
    </div>
  )
}

function DeletedBlock({ content }: { content: string }) {
  return (
    <div className="rounded-md overflow-hidden">
      <div
        className="px-3 py-2 text-sm"
        style={{
          backgroundColor: 'hsl(0 50% 50% / 0.08)',
          borderLeft: '3px solid hsl(0 50% 55%)',
        }}
      >
        <div
          className="font-serif leading-relaxed line-through prose-diff-deleted"
          style={{ color: 'hsl(0 50% 35%)', textDecorationColor: 'hsl(0 50% 55%)' }}
        >
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      </div>
    </div>
  )
}

/**
 * Compute word-level diff between two strings.
 * Returns segments with type: 'same', 'deleted', or 'added'
 */
function computeWordDiff(oldText: string, newText: string): Array<{ type: 'same' | 'deleted' | 'added'; text: string }> {
  const oldWords = oldText.split(/(\s+)/)
  const newWords = newText.split(/(\s+)/)

  const segments: Array<{ type: 'same' | 'deleted' | 'added'; text: string }> = []

  // Simple LCS-based diff
  const m = oldWords.length
  const n = newWords.length

  // Build LCS table
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
      }
    }
  }

  // Backtrack to find diff
  let i = m, j = n
  const result: Array<{ type: 'same' | 'deleted' | 'added'; text: string }> = []

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      result.unshift({ type: 'same', text: oldWords[i - 1] })
      i--
      j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: 'added', text: newWords[j - 1] })
      j--
    } else {
      result.unshift({ type: 'deleted', text: oldWords[i - 1] })
      i--
    }
  }

  // Merge consecutive segments of same type
  for (const seg of result) {
    if (segments.length > 0 && segments[segments.length - 1].type === seg.type) {
      segments[segments.length - 1].text += seg.text
    } else {
      segments.push({ ...seg })
    }
  }

  return segments
}

/**
 * Render inline word-level diff with proper styling.
 * Renders markdown formatting (italics, bold) while highlighting changes.
 */
function ModifiedBlock({ deleted, added }: { deleted: string; added: string }) {
  const segments = computeWordDiff(deleted, added)

  return (
    <div className="rounded-md overflow-hidden border border-border">
      <div className="px-3 py-2 text-sm bg-background">
        <p className="font-serif leading-relaxed">
          {segments.map((seg, idx) => {
            // Render markdown-style formatting within the segment
            const renderText = (text: string) => {
              // Handle *italic* and **bold** patterns
              const parts: React.ReactNode[] = []
              let remaining = text
              let key = 0

              while (remaining.length > 0) {
                // Check for **bold**
                const boldMatch = remaining.match(/^\*\*(.+?)\*\*/)
                if (boldMatch) {
                  parts.push(<strong key={key++}>{boldMatch[1]}</strong>)
                  remaining = remaining.slice(boldMatch[0].length)
                  continue
                }

                // Check for *italic*
                const italicMatch = remaining.match(/^\*(.+?)\*/)
                if (italicMatch) {
                  parts.push(<em key={key++}>{italicMatch[1]}</em>)
                  remaining = remaining.slice(italicMatch[0].length)
                  continue
                }

                // Find next markdown marker
                const nextMarker = remaining.search(/\*/)
                if (nextMarker === -1) {
                  parts.push(remaining)
                  break
                } else if (nextMarker === 0) {
                  // Unmatched marker, render as-is
                  parts.push(remaining[0])
                  remaining = remaining.slice(1)
                } else {
                  parts.push(remaining.slice(0, nextMarker))
                  remaining = remaining.slice(nextMarker)
                }
              }

              return parts.length === 1 ? parts[0] : parts
            }

            if (seg.type === 'same') {
              return <span key={idx}>{renderText(seg.text)}</span>
            }
            if (seg.type === 'deleted') {
              return (
                <span
                  key={idx}
                  className="line-through"
                  style={{
                    backgroundColor: 'hsl(0 50% 50% / 0.15)',
                    color: 'hsl(0 50% 35%)',
                    textDecorationColor: 'hsl(0 50% 45%)',
                    borderRadius: '2px',
                    padding: '0 2px',
                  }}
                >
                  {renderText(seg.text)}
                </span>
              )
            }
            if (seg.type === 'added') {
              return (
                <span
                  key={idx}
                  style={{
                    backgroundColor: 'hsl(142 50% 50% / 0.15)',
                    color: 'hsl(142 50% 25%)',
                    borderRadius: '2px',
                    padding: '0 2px',
                  }}
                >
                  {renderText(seg.text)}
                </span>
              )
            }
            return null
          })}
        </p>
      </div>
    </div>
  )
}

function ChangeCard({ change }: { change: Change }) {
  if (change.type === 'added') {
    return <AddedBlock content={change.content} />
  }

  if (change.type === 'deleted') {
    return <DeletedBlock content={change.content} />
  }

  if (change.type === 'modified' && change.modifications?.[0]) {
    return (
      <ModifiedBlock
        deleted={change.modifications[0].deleted}
        added={change.modifications[0].added}
      />
    )
  }

  return null
}

export function DDiffViewer({ finalDiff, onClose }: DDiffViewerProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const changes = (finalDiff.changes || []) as Change[]

  // Don't show if no changes or no detailed changes to display
  if (!finalDiff.has_changes || changes.length === 0) {
    return null
  }

  return (
    <div className="border border-border rounded-md overflow-hidden bg-background">
      {/* Header with stats - always visible */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-light text-foreground">Editing Summary</span>
          <DiffStatsCard stats={finalDiff.stats} />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            className="p-1.5 hover:bg-muted rounded-md transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          </button>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          )}
        </div>
      </div>

      {/* Expandable change cards */}
      {isExpanded && (
        <div className="border-t border-border max-h-[400px] overflow-y-auto">
          <div className="p-4 space-y-3">
            {changes.map((change, index) => (
              <ChangeCard key={index} change={change} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
